import React from 'react';
import ErrorFallback from '../../../../common/ErrorFallback';
import { DetailItem } from '../../../../common/RenderDetails';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Empty, Spin, Typography, Descriptions, Card, Divider, Tabs, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { VideoRecording, ErrorLog, ErrorType } from '../types/visa-application.types';

const { Title } = Typography;

export interface VisaApplicationDetailsComponentProps {
  sections: SectionData[];
  videoRecordings?: VideoRecording[];
  errorLogs?: ErrorLog[];
  success: boolean;
  isLoading: boolean;
  error: Error | null;
}

export interface SectionData {
  title: string;
  items: DetailItem[];
}

const VisaApplicationDetailsComponent = ({
  sections,
  videoRecordings,
  errorLogs,
  success,
  isLoading,
  error,
}: VisaApplicationDetailsComponentProps) => {
  const { t } = useTranslation();

  if (!sections && !isLoading) return <Empty description={t('crud.details.noData')} />;

  if (error)
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={error}
        resetErrorBoundary={() => {}}
      />
    );

  // Filter video recordings that have a videoUrl
  const validVideoRecordings =
    videoRecordings?.filter((recording) => recording.videoUrl && !recording.isDeleted) || [];

  const hasVideoRecordings = validVideoRecordings.length > 0;

  // Filter error logs that are not deleted
  const validErrorLogs = errorLogs?.filter((log) => !log.isDeleted) || [];
  const hasErrorLogs = validErrorLogs.length > 0;

  const formatErrorType = (errorType?: ErrorType) => {
    switch (errorType) {
      case ErrorType.BOT_ERROR:
        return t('visaApplications.errorLogs.botError', 'Bot Error');
      case ErrorType.DATA_ERROR:
        return t('visaApplications.errorLogs.dataError', 'Data Error');
      default:
        return errorType || '';
    }
  };

  const detailTabContent = (
    <Card className="shadow-lg">
      {hasErrorLogs && (
        <>
          <style>
            {`
              .custom-error-alert .ant-alert-icon {
                color: #ff4d4f !important;
                font-size: 26px !important;
              }
            `}
          </style>
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={
                <span style={{ color: '#ff4d4f', fontWeight: 700 }}>
                  {t('visaApplications.errorLogs.title') || 'Error'}
                </span>
              }
              description={
                <div style={{ color: '#ff4d4f' }}>
                  {validErrorLogs.map((log, index) => (
                    <div
                      key={`${log.dataSource}-${log.dataId}-${index}`}
                      style={{ marginBottom: index < validErrorLogs.length - 1 ? 12 : 0 }}
                    >
                      <div className="row" style={{ marginBottom: 4 }}>
                        {log.meta?.errorType && (
                          <div className="col-md-4 col-sm-12">
                            <span style={{ fontWeight: 700 }}>
                              {t('visaApplications.errorLogs.errorTypeLabel', 'Error Type')}:
                            </span>{' '}
                            {formatErrorType(log.meta.errorType)}
                          </div>
                        )}
                        {log.meta?.step && (
                          <div className="col-md-4 col-sm-12">
                            <span style={{ fontWeight: 700 }}>
                              {t('visaApplications.errorLogs.stepLabel', 'Error Step No')}:
                            </span>{' '}
                            {log.meta.step}
                          </div>
                        )}
                        {log.meta?.fieldName && (
                          <div className="col-md-4 col-sm-12">
                            <span style={{ fontWeight: 700 }}>
                              {t('visaApplications.errorLogs.fieldNameLabel', 'Error Field Name')}:
                            </span>{' '}
                            {log.meta.fieldName}
                          </div>
                        )}
                      </div>
                      <div>
                        <strong>
                          {t('visaApplications.errorLogs.messageLabel', 'Error Message')}:
                        </strong>{' '}
                        {log.errorMessage || t('visaApplications.errorLogs.noMessage')}
                      </div>
                    </div>
                  ))}
                </div>
              }
              type="error"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{
                borderColor: '#ff4d4f',
                backgroundColor: '#fff2f0',
              }}
              className="custom-error-alert"
            />
          </div>
        </>
      )}
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={sectionIndex}>
          {sectionIndex > 0 && <Divider className="my-6" />}
          <Title level={5}>{section.title}</Title>
          <Descriptions column={3} bordered>
            {section.items.map((item, itemIndex) => (
              <Descriptions.Item key={itemIndex} label={t(item.label)}>
                {item.value}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </React.Fragment>
      ))}
    </Card>
  );

  const videoRecordingTabContent = (
    <Card className="shadow-lg">
      {validVideoRecordings.length === 0 ? (
        <Empty description={t('common.noData')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {validVideoRecordings.map((recording, index) => (
            <div key={recording.id}>
              {index > 0 && <Divider />}
              <Title level={5}>
                {t('visaApplications.videoRecording')}{' '}
                {validVideoRecordings.length > 1 ? `${index + 1}` : ''}
              </Title>
              <div
                style={{
                  width: '100%',
                  maxWidth: '70%',
                  margin: '0 auto',
                }}
              >
                <video
                  controls
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                  }}
                  src={recording.videoUrl}
                >
                  {t('common.mediaPreview.videoNotSupported')}
                </video>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const tabItems = [
    {
      key: 'detail',
      label: t('visaApplications.details') || 'Visa Applications Details',
      children: detailTabContent,
    },
  ];

  if (hasVideoRecordings) {
    tabItems.push({
      key: 'video',
      label: t('visaApplications.videoRecording') || 'Video Recording',
      children: videoRecordingTabContent,
    });
  }

  return (
    <Spin spinning={isLoading} tip={t('common.loading')}>
      <Tabs items={tabItems} defaultActiveKey="detail" />
    </Spin>
  );
};

const areEqual = (
  prevProps: VisaApplicationDetailsComponentProps,
  nextProps: VisaApplicationDetailsComponentProps
): boolean => {
  if (
    prevProps.isLoading !== nextProps.isLoading ||
    prevProps.success !== nextProps.success ||
    prevProps.error !== nextProps.error
  ) {
    return false;
  }

  if (prevProps.sections.length !== nextProps.sections.length) {
    return false;
  }

  const sectionsEqual = prevProps.sections.every((section, sectionIndex) => {
    const nextSection = nextProps.sections[sectionIndex];
    if (section.items.length !== nextSection.items.length) return false;
    return section.items.every((item, itemIndex) => {
      const nextItem = nextSection.items[itemIndex];
      return item.label === nextItem.label && item.value === nextItem.value;
    });
  });

  if (!sectionsEqual) return false;

  // Compare videoRecordings
  const prevRecordings = prevProps.videoRecordings || [];
  const nextRecordings = nextProps.videoRecordings || [];

  if (prevRecordings.length !== nextRecordings.length) {
    return false;
  }

  const recordingsEqual = prevRecordings.every((prevRecording, index) => {
    const nextRecording = nextRecordings[index];
    return (
      prevRecording.id === nextRecording.id &&
      prevRecording.videoUrl === nextRecording.videoUrl &&
      prevRecording.isDeleted === nextRecording.isDeleted
    );
  });

  if (!recordingsEqual) return false;

  // Compare errorLogs
  const prevErrorLogs = prevProps.errorLogs || [];
  const nextErrorLogs = nextProps.errorLogs || [];

  if (prevErrorLogs.length !== nextErrorLogs.length) {
    return false;
  }

  return prevErrorLogs.every((prevLog, index) => {
    const nextLog = nextErrorLogs[index];

    return (
      prevLog.dataSource === nextLog.dataSource &&
      prevLog.dataId === nextLog.dataId &&
      prevLog.errorMessage === nextLog.errorMessage &&
      prevLog.isDeleted === nextLog.isDeleted &&
      prevLog.meta?.errorType === nextLog.meta?.errorType &&
      prevLog.meta?.step === nextLog.meta?.step &&
      prevLog.meta?.fieldName === nextLog.meta?.fieldName
    );
  });
};

export default React.memo(VisaApplicationDetailsComponent, areEqual);
